import type { RefObject } from 'react';
import { useEffect, useCallback } from 'react';
import type { RootId } from '~/components/molecule/Portal';

/**
 * When you make changes to this hook, please check that it works with the following scenarios:
 * - Inserting deep links in SendEmailAction (Hovering toolbar portal > Insert deep link portal)
 * - Contact filters date selection (Filter selector portal > Date picker portal)
 * - LFA time picker does not get closed when address search result returns with a delay (When testing unique ids for portals)
 * - Condition editor in flows (select values from dropdowns)
 * - Hovering toolbar in rich text editor for images, links, etc. inside SendEmailAction
 */

type Params = {
  /**
   * If provided, the click will be allowed to pass through the portal
   */
  allowClickInPortals?: Array<RootId>;

  /**
   * If provided, the click will be allowed to pass through the refs
   */
  allowClickInRefs?: Array<RefObject<HTMLElement>>;

  /**
   * The callback to call when the click is outside of the element
   */
  callback: (e: MouseEvent | TouchEvent) => void;
};

/**
 * Checks whether a click registered outside the element
 *
 * keywords: click,outside,element
 */
const useClickOutside = ({
  allowClickInPortals,
  allowClickInRefs,
  callback,
}: Params) => {
  const handleClick = useCallback(
    (e: MouseEvent | TouchEvent) => {
      // Check if click is inside allowed portals - if yes, don't call callback
      if (allowClickInPortals && e.target instanceof Node) {
        if (
          allowClickInPortals.some(root => {
            const portalRoot = document.getElementById(`${root}-root`);
            if (!portalRoot) {
              return false;
            }
            return portalRoot.contains(e.target as Node);
          })
        ) {
          return;
        }
      }

      // Check if click is inside allowed refs - if yes, don't call callback
      if (
        allowClickInRefs &&
        e.target instanceof Node &&
        allowClickInRefs.some(ref => ref.current?.contains(e.target as Node))
      ) {
        return;
      }

      // All checks passed - click is outside, call callback
      callback(e);
    },
    [callback, allowClickInPortals, allowClickInRefs],
  );

  useEffect(() => {
    // Passing true catches event in capture phase so that useClickOutside works when clicking inside the modals as well.
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [handleClick]);
};

export default useClickOutside;
